import Store from '@ember-data/store';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isNumber } from 'donor-elf-web/lib/string-utils';
import CampaignGoalModel from 'donor-elf-web/models/campaign-goal';
import AddSomethingModal, {
  type OpenArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import FormatAmountService from 'donor-elf-web/services/format-amount-service';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import type { ValidationResult } from 'ember-changeset/types';

interface ComponentArgs {
  openArgs?: OpenArgs;
}

export default class AddSomethingModalCampaignGoalIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.createChangeset();
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare formatAmountService: FormatAmountService;
  @service declare store: Store;
  @service declare campaignService: CampaignService;

  @tracked changeset: any;
  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked saveError?: any;

  get newCampaignGoal() {
    return this.args.openArgs?.campaignGoalOptions?.campaignGoal == null;
  }

  get campaign() {
    return this.args.openArgs!.campaignGoalOptions!.campaign;
  }

  createChangeset() {
    let campaign = this.newCampaignGoal
      ? this.store.createRecord('campaignGoal', {
          campaignId: this.campaign.id,
        })
      : this.args.openArgs!.campaignGoalOptions!.campaignGoal!;

    let { goalValidation } = this;
    this.changeset = new Changeset(
      campaign,
      lookupValidator(goalValidation),
      goalValidation,
    );
  }

  get goalValidation() {
    return {
      goalAmount: this.validateGoalAmount,
    };
  }

  validateGoalAmount = (
    _key: string,
    newValue: string,
    _oldValue: string,
    _changes: any,
    _content: any,
  ): ValidationResult => {
    if (isPresent(newValue)) {
      if (isNumber(newValue)) {
        let newGoalAmount = Number(newValue);
        if (this.parentCampaignGoalAmount) {
          if (newGoalAmount > this.parentCampaignGoalAmount) {
            return `Can't be higher than its parent campaign goal amount (${this.formatAmountService.format(this.childCampaignsGoalAmountSum, { roundToDollar: true })})`;
          } else if (
            newGoalAmount + this.siblingsCampaignGoalAmountSum >
            this.parentCampaignGoalAmount
          ) {
            let combinedSiblingGoalSum =
              newGoalAmount + this.siblingsCampaignGoalAmountSum;
            return `The sum of the sibling campaign goals (${this.formatAmountService.format(combinedSiblingGoalSum, { roundToDollar: true })}) can't be higher than the parent campaign goal (${this.formatAmountService.format(this.parentCampaignGoalAmount, { roundToDollar: true })})`;
          }
        }
        if (this.campaign.childrenCampaigns.length > 0) {
          if (newGoalAmount < this.childCampaignsGoalAmountSum) {
            return `Can't be lower than the sum of its child campaign goals (${this.formatAmountService.format(this.childCampaignsGoalAmountSum, { roundToDollar: true })})`;
          }
        }
      } else {
        return 'Must be a number (no comma or $)';
      }
    } else {
      return "Can't be blank";
    }
    return true;
  };

  get parentCampaignGoalAmount() {
    return this.campaignService.activeCampaignGoalsByCampaignId[
      this.campaign.parentCampaignId!
    ]?.goalAmount;
  }

  get siblingsCampaignGoalAmountSum() {
    if (this.campaign.parentCampaign == null) {
      return 0;
    }
    let siblings = this.campaign.parentCampaign.childrenCampaigns.filter(
      (x) => x !== this.campaign,
    );
    return siblings
      .map((camp) => {
        return (
          this.campaignService.activeCampaignGoalsByCampaignId[camp.id]
            ?.goalAmount || 0
        );
      })
      .reduce((sum: number, val: number) => {
        return (sum += val);
      }, 0);
  }

  get childCampaignsGoalAmountSum() {
    return this.campaign.childrenCampaigns
      .map((camp) => {
        return (
          this.campaignService.activeCampaignGoalsByCampaignId[camp.id]
            ?.goalAmount || 0
        );
      })
      .reduce((sum: number, val: number) => {
        return (sum += val);
      }, 0);
  }

  save = async () => {
    if (this.saving) {
      return;
    }
    this.showValidationErrors = false;
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      try {
        this.saving = true;
        await this.changeset.save();
        let campaignGoal = this.changeset.get('data') as CampaignGoalModel;
        this.addSomethingModal.campaignGoalSaved({
          campaign: this.campaign,
          campaignGoal,
          newCampaignGoal: this.newCampaignGoal,
        });
        this.addSomethingModal.close();
      } catch (e) {
        this.saveError = e;
      }
      this.saving = false;
    } else {
      this.showValidationErrors = true;
    }
  };

  close = () => {
    this.addSomethingModal.close();
  };
}
